import { useEffect, useMemo, useState } from "react";
import { gameApi } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, ShoppingBag, Check, Loader2, Info } from "lucide-react";

export const TackRoom = ({ keptHorses = [], onBack, onUpdate }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [equipHorse, setEquipHorse] = useState(keptHorses[0]?.id || "");
  const [busy, setBusy] = useState(false);

  const load = () => { setLoading(true); gameApi.get("/game/shop/cosmetics").then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false)); };
  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps

  const owned = data?.owned || [];
  const equipped = data?.equipped || {};
  const tack = (data?.items || []).filter((i) => i.section === "tack" && owned.includes(i.id));
  const byCat = useMemo(() => { const m = {}; tack.forEach((it) => { (m[it.category] = m[it.category] || []).push(it); }); return m; }, [tack]);
  const isEquipped = (it) => (equipHorse ? equipped[equipHorse]?.[it.slot] === it.id : Object.values(equipped).some((h) => h[it.slot] === it.id));

  const equip = async (it) => {
    if (!equipHorse) { toast.error("Keep a horse first to equip tack."); return; }
    setBusy(true);
    try { const { data: res } = await gameApi.post("/game/cosmetics/equip", { item_id: it.id, horse_id: equipHorse }); toast.success(`Equipped ${it.name}`); if (res.player) onUpdate && onUpdate(res.player); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not equip."); } finally { setBusy(false); }
  };
  const unequip = async (it) => {
    setBusy(true);
    try {
      const hid = equipHorse || Object.keys(equipped).find((h) => equipped[h][it.slot] === it.id);
      const { data: res } = await gameApi.post("/game/cosmetics/unequip", { section: "tack", slot: it.slot, horse_id: hid });
      toast.success(`Unequipped ${it.name}`); if (res.player) onUpdate && onUpdate(res.player); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not unequip."); } finally { setBusy(false); }
  };

  return (
    <div className="pb-10" data-testid="tackroom-view">
      <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-border bg-card/95 px-4 py-3 backdrop-blur">
        <button data-testid="tackroom-back" onClick={onBack} className="rounded-full p-1 text-muted-foreground"><ArrowLeft size={20} /></button>
        <ShoppingBag className="text-secondary" size={20} /><h2 className="font-display text-xl font-bold text-primary">Tack Room</h2>
      </div>
      <div className="px-4 pt-3">
        {keptHorses.length > 0 ? (
          <div className="mb-3 flex items-center gap-2">
            <span className="text-xs font-semibold text-muted-foreground">Horse:</span>
            <select data-testid="tackroom-horse-select" value={equipHorse} onChange={(e) => setEquipHorse(e.target.value)} className="flex-1 rounded-lg border border-border bg-background px-2 py-1.5 text-xs font-semibold text-primary">
              {keptHorses.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
            </select>
          </div>
        ) : (
          <p className="mb-3 flex items-center gap-2 rounded-xl border border-dashed border-border p-3 text-xs text-muted-foreground"><Info size={14} /> Keep a horse to equip tack on it.</p>
        )}
        {loading ? <p className="mt-6 flex items-center justify-center gap-2 text-sm text-muted-foreground"><Loader2 className="animate-spin" size={16} /> Loading your tack…</p>
          : tack.length === 0 ? <p className="mt-6 rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground" data-testid="tackroom-empty">You don't own any tack yet. Buy tack in the Tack &amp; Barn Shop.</p>
          : Object.entries(byCat).map(([cat, items]) => (
            <div key={cat} className="mb-4">
              <p className="mb-1.5 text-xs font-bold uppercase tracking-wider text-secondary">{cat}</p>
              <div className="grid grid-cols-2 gap-2">
                {items.map((it) => {
                  const eq = isEquipped(it);
                  return (
                    <div key={it.id} data-testid={`tack-item-${it.id}`} className="rounded-2xl border border-border bg-card p-2.5">
                      <div className="relative h-16 w-full overflow-hidden rounded-xl" style={{ background: it.color }}>{eq && <span className="absolute left-1 top-1 rounded-full bg-white/90 px-1.5 py-0.5 text-[9px] font-bold text-green-700">Equipped</span>}</div>
                      <p className="mt-1.5 truncate text-xs font-bold text-primary">{it.name}</p>
                      {eq
                        ? <button data-testid={`tack-unequip-${it.id}`} disabled={busy} onClick={() => unequip(it)} className="mt-1.5 h-8 w-full rounded-lg border border-border text-xs font-bold text-primary active:scale-95">Unequip</button>
                        : <button data-testid={`tack-equip-${it.id}`} disabled={busy || !equipHorse} onClick={() => equip(it)} className="mt-1.5 h-8 w-full rounded-lg bg-primary text-xs font-bold text-primary-foreground disabled:opacity-50 active:scale-95"><Check size={12} className="mr-1 inline" />Equip</button>}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
      </div>
    </div>
  );
};
